import dayjs from 'dayjs'
import html2pdf from 'html2pdf.js'

const formatDate = (date) => {
  if (!date) return '____년 __월 __일'
  try {
    return dayjs(date).format('YYYY년 MM월 DD일')
  } catch {
    return '____년 __월 __일'
  }
}

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null || amount === '') return ''
  try {
    const normalized =
      typeof amount === 'string'
        ? parseInt(amount.replace(/,/g, ''), 10)
        : Number(amount)
    if (Number.isNaN(normalized)) return ''
    return normalized.toLocaleString()
  } catch {
    return ''
  }
}

const getCompanyInfo = (data) => ({
  companyName: data?.companyName || '주식회사 하우투약',
  companyPhone: data?.companyPhone || '02-555-0590',
  companyAddress: data?.companyAddress || '서울시 강남구 테헤란로 501, 5층',
  representativeName: data?.representativeName || '김경열',
})

// 연봉 계약서 템플릿
const buildSalaryContractHtml = (data) => {
  const { companyName, companyPhone, companyAddress, representativeName } =
    getCompanyInfo(data)

  // 템플릿에서 받은 발급 번호를 그대로 사용 (날짜 기반 생성 제거)
  const issueNumber = data?.certificateNo || '____'

  return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
              font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
              color: #191a1c;
              line-height: 1.6;
              width: 210mm;
              background: white;
            }
          </style>
        </head>
        <body style="letter-spacing: -0.03em;">
          <!-- 첫 번째 페이지 -->
          <div style="width: 210mm; height: auto; background: white; box-sizing: border-box; display: block; position: relative;">
            <!-- 장식 헤더 -->
            <div style="margin-bottom: 30px;">
              <div style="position: relative; width: 100%; height: 26px; background: #f0f0f0; overflow: hidden;">
                <div style="position: absolute; left: 0; top: 0; width: 66.67%; height: 100%; background: #007AFF; clip-path: polygon(0 0, calc(100% - 26px) 0, 100% 100%, 0 100%);"></div>
                <div style="position: absolute; right: 0; top: 0; width: 33.33%; height: 100%; background: #f0f0f0;"></div>
              </div>
            </div>

            <div style="padding: 20mm; letter-spacing: -0.03em;">
            <div style="position: relative; margin-bottom: 40px;">
              <h1 style="font-size: 24px; font-weight: 700; margin: 0; text-align: center;">연봉 계약서</h1>
              <div style="position: absolute; right: 0; top: 50%; transform: translateY(-50%); font-size: 14px;">발급 번호: ${issueNumber}</div>
            </div>

            <div>   
              <h3 style="font-size: 12px; font-weight: 600; margin: 0 0 20px 0;">주식회사 하우투약 (이하 "사업주"라 함) ${data?.employeeName || '____'} (이하 "근로자"라 함)은 다음과 같이 연봉계약을 체결한다.
              </h3>
            </div>  

            <div>
              <!-- 계약기간 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">1. 계약기간</h2>
                <p style="font-size: 12px; font-weight: 400; margin: 8px 0; line-height: 1.8; color: #383E44;">
                  ${formatDate(data?.contractStartDate)} 부터 
                  ${data?.contractEndDate ? formatDate(data?.contractEndDate) + '까지' : ''}
                </p>
              </section>

              <!-- 소속정보 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">2. 소속정보</h2>
                <div style="display: flex; flex-direction: column; gap: 4px;">
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">소속 부서:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">${data?.department || ''}</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">직무:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">${data?.position || ''}</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">직급:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">${data?.title || '사원'}</span>
                  </div>
                </div>
              </section>

              <!-- 근무조건 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">3. 근무조건</h2>
                <div style="display: flex; flex-direction: column; gap: 4px;">
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">근무형태:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">${data?.workTypeDisplay || ''}</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">근무시간:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">
                      ${data?.workStartTime || ''}시 ${data?.workStartMinute || ''} 분부터 
                      ${data?.workEndTime || ''}시 ${data?.workEndMinute || ''}분까지
                      ${data?.breakStartTime && data?.breakEndTime ? ` (휴게시간: ${data.breakStartTime}시 ${data.breakStartMinute}분 ~ ${data.breakEndTime}시 ${data.breakEndMinute}분)` : ''}
                    </span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">근무장소:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">${data?.workplace || ''}</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">근무요일 / 휴무일:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px;">매주 5일 근무, 주휴일, 매주 일요일</span>
                  </div>
                </div>
              </section>

              <!-- 연봉 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">4. 연봉</h2>
                <div style="display: flex; flex-direction: column; gap: 4px;">
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">연봉총액:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">금 ${formatCurrency(data?.totalSalary) || '-'}원 (연봉 
                      ${data?.totalSalary ? formatCurrency(data.totalSalary) : '-'}원)</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">급여구성:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">
                      기본급 (${data?.basicSalary ? formatCurrency(data.basicSalary) : '____'}), 
                      고정상여 (${data?.fixedAllowance ? formatCurrency(data.fixedAllowance) : '____'}), 
                      인센티브 (${data?.incentive ? formatCurrency(data.incentive) : '____'})
                    </span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">지급방식:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px;">월 분할 지급</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">지급주기:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px;">매월</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">지급기준일:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px;">매월 05일</span>
                  </div>
                </div>
              </section>

              <!-- 상여 / 성과급 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">5. 상여 / 성과급</h2>
                <div style="display: flex; flex-direction: column; gap: 12px;">
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">지급여부:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px;">
                      <span style="display: inline-block; margin-right: 20px;">
                        있음 (✓)
                      </span>
                      <span style="display: inline-block;">
                        없음 ()
                      </span>
                    </span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">지급기준:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px;">개인 성과 / 조직 성과 기준 / 회사 내부 기준에 따름</span>
                  </div>
                </div>
              </section>

              <!-- 퇴직금 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">6. 퇴직금</h2>
                <p style="font-size: 14px; margin: 8px 0; line-height: 1.8;">근로기준법에서 정하는 바에 따라 지급함</p>
              </section>
            </div>

              <!-- 하우투약 푸터 (고정 위치) -->
              <div style="position: absolute; bottom: 16mm; left: 50%; transform: translateX(-50%); display: flex; justify-content: center; align-items: center; gap: 8px; font-size: 10px; color: #9CA3AF; width: 100%;">
                <img src="/assets/icon/aops/howtoyak-logo.png" style="height: 16px; width: auto; display: block;" />
                <span style="font-weight: 600; letter-spacing: 0.08em;">HOW TO YAK</span>
              </div>
            </div>
          </div>

          <!-- 두 번째 페이지 -->
          <div style="width: 210mm; height: auto; background: white; box-sizing: border-box; page-break-before: always; display: block; position: relative;">
            <!-- 장식 헤더 -->
            <div style="margin-bottom: 30px;">
              <div style="position: relative; width: 100%; height: 26px; background: #f0f0f0; overflow: hidden;">
                <div style="position: absolute; left: 0; top: 0; width: 66.67%; height: 100%; background: #007AFF; clip-path: polygon(0 0, calc(100% - 26px) 0, 100% 100%, 0 100%);"></div>
                <div style="position: absolute; right: 0; top: 0; width: 33.33%; height: 100%; background: #f0f0f0;"></div>
              </div>
            </div>

            <div style="padding: 20mm; letter-spacing: -0.03em;">
              <!-- 연봉조정 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">7. 연봉조정</h2>
                <div style="display: flex; flex-direction: column; gap: 12px;">
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">조정시기:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">매년 1회</span>
                  </div>
                  <div style="display: flex; align-items: baseline; font-size: 14px;">
                    <span style="font-weight: 500; min-width: 120px; margin-right: 12px;">재계약 조건:</span>
                    <span style="flex: 1; border-bottom: 1px solid #d8e1e6; padding-bottom: 2px; color: #383E44;">성과 평가 및 회사 정책에 따름</span>
                  </div>
                </div>
              </section>

              <!-- 연차유급휴가 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">8. 연차유급휴가</h2>
                <p style="font-size: 14px; margin: 8px 0; line-height: 1.8;">연차 유급 휴가는 근로기준법에서 정하는 바에 따라 부여함</p>
              </section>

              <!-- 사회보험 적용여부 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">9. 사회보험 적용여부 (해당란에 체크)</h2>
                <div style="display: flex; gap: 24px; flex-wrap: wrap;">
                  <label style="display: flex; align-items: center; gap: 8px; font-size: 14px;">
                    <input type="checkbox" ${
                      data?.employmentInsurance ? 'checked' : ''
                    } disabled style="width: 16px; height: 16px;" />
                    고용보험
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; font-size: 14px;">
                    <input type="checkbox" ${
                      data?.industrialAccidentInsurance ? 'checked' : ''
                    } disabled style="width: 16px; height: 16px;" />
                    산재보험
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; font-size: 14px;">
                    <input type="checkbox" ${
                      data?.nationalPension ? 'checked' : ''
                    } disabled style="width: 16px; height: 16px;" />
                    국민연금
                  </label>
                  <label style="display: flex; align-items: center; gap: 8px; font-size: 14px;">
                    <input type="checkbox" ${
                      data?.healthInsurance ? 'checked' : ''
                    } disabled style="width: 16px; height: 16px;" />
                    건강보험
                  </label>
                </div>
              </section>

              <!-- 연봉계약서 교부 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">10. 연봉계약서 교부</h2>
                <p style="font-size: 14px; margin: 8px 0; line-height: 1.8;">
                  사업주는 연봉계약을 체결함과 동시에 본 계약서를 사본하여 근로자의
                  교부요구와 관계없이 근로자에게 교부함 (근로기준법 제 17조 이행)
                </p>
              </section>

              <!-- 기타 -->
              <section style="margin-bottom: 30px;">
                <h2 style="font-size: 13px; font-weight: 700; margin: 0 0 12px 0;">11. 기타</h2>
                <p style="font-size: 14px; margin: 8px 0; line-height: 1.8;">이 계약에 정함이 없는 사항은 근로기준법령에 의함</p>
              </section>

              <!-- 서명란 -->
              <div style="margin-top: 60px;">
                <div style="text-align: center; margin-bottom: 40px; font-size: 14px; color: #383E44;">${formatDate(data?.contractDate)}</div>

                <div style="display: flex; flex-direction: column; gap: 40px;">
                  <div style="flex: 1;  padding: 20px; border-radius: 8px; display: flex; flex-direction: row; gap: 10px; align-items: flex-start;">
                    <div style="font-size: 12px; gap: 4px; position: relative;">
                      <p style="margin: 4px 0; color: #383E44;">(사업주) 사업체명: ${companyName} (전화: ${companyPhone})</p>
                      <p style="margin: 4px 0; color: #383E44;">주소: ${companyAddress}</p>
                      <p style="margin: 4px 0; color: #383E44; position: relative;">
                        대표자: ${representativeName} (서명)
                        <img src="/assets/seal.png" style="position: absolute; left: 70px; top: -10px; width: 60px; height: 60px; display: block;" />
                      </p>
                    </div>
                  </div>
                  <div style="flex: 1;  padding: 20px; border-radius: 8px; display: flex; flex-direction: row; gap: 10px; align-items: flex-start;">
                    <div style="font-size: 12px; gap: 4px;">
                      <p style="margin: 4px 0; color: #383E44;">(근로자) 주소: ${data?.employeeAddress || '____'}</p>
                      <p style="margin: 4px 0; color: #383E44;">연락처: ${data?.employeePhone || '____'}</p>
                      <p style="margin: 4px 0; color: #383E44;">주민등록번호: ${data?.employeeRRN || data?.employeeBirthDate || '____'}</p>
                      <p style="margin: 4px 0; color: #383E44;">성명: ${data?.employeeName || '김아무개'} (서명)</p>
                    </div>
                  </div>
                </div>

                <!-- 하우투약 푸터 (고정 위치) -->
                <div style="position: absolute; bottom: 16mm; left: 50%; transform: translateX(-50%); display: flex; justify-content: center; align-items: center; gap: 8px; font-size: 10px; color: #9CA3AF; width: 100%;">
                  <img src="/assets/icon/aops/howtoyak-logo.png" style="height: 16px; width: auto; display: block;" />
                  <span style="font-weight: 600; letter-spacing: 0.08em;">HOW TO YAK</span>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `
}

// 공통 PDF 생성 로직
const createPdfFromHtml = async (htmlContent) => {
  const iframe = document.createElement('iframe')
  iframe.style.position = 'absolute'
  iframe.style.left = '-9999px'
  iframe.style.width = '210mm'
  iframe.style.height = '600mm'
  iframe.style.border = 'none'
  document.body.appendChild(iframe)

  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
  iframeDoc.open()
  iframeDoc.write(htmlContent)
  iframeDoc.close()

  await new Promise((resolve) => setTimeout(resolve, 1500))

  const body = iframeDoc.body

  let checkCount = 0
  await new Promise((resolve) => {
    const checkRender = () => {
      checkCount++
      const pages = body.querySelectorAll('div')
      const hasContent = body.textContent && body.textContent.trim().length > 0
      const hasHeight = body.offsetHeight > 0

      if (hasContent && hasHeight && pages.length >= 2) {
        setTimeout(resolve, 500)
      } else if (checkCount > 100) {
        console.warn('렌더링 확인 실패, 계속 진행합니다.')
        console.log('body 높이:', body.offsetHeight)
        console.log('body 내용:', body.textContent?.substring(0, 100))
        console.log('div 개수:', pages.length)
        resolve()
      } else {
        setTimeout(checkRender, 50)
      }
    }
    checkRender()
  })

  const options = {
    margin: [0, 0, 0, 0],
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
      logging: false,
      allowTaint: true,
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  }

  const pdfBlob = await html2pdf().set(options).from(body).outputPdf('blob')

  document.body.removeChild(iframe)

  return pdfBlob
}

/**
 * 연봉계약서 PDF 생성
 * @param {Object} data - 계약서 데이터
 * @returns {Promise<Blob>}
 */
export const generateSalaryContractPdf = async (data) => {
  const htmlContent = buildSalaryContractHtml(data)

  try {
    return await createPdfFromHtml(htmlContent)
  } catch (error) {
    console.error('연봉계약서 PDF 생성 오류:', error)
    throw error
  }
}
